import toast, { ToastOptions } from 'react-hot-toast';

/**
 * Toast Service
 * Centralized toast notification service for consistent messaging across the app.
 */

const defaultOptions: ToastOptions = {
  duration: 4000,
  position: 'bottom-right',
};

const toastService = {
  /**
   * Show a success toast
   */
  success: (message: string, options?: ToastOptions) => {
    return toast.success(message, { ...defaultOptions, ...options });
  },

  /**
   * Show an error toast
   */
  error: (message: string, options?: ToastOptions) => {
    return toast.error(message, { ...defaultOptions, duration: 5000, ...options });
  },

  /**
   * Show a loading toast (returns dismiss function)
   */
  loading: (message: string, options?: ToastOptions) => {
    return toast.loading(message, { ...defaultOptions, ...options });
  },

  /**
   * Dismiss a specific toast or all toasts
   */
  dismiss: (toastId?: string) => {
    toast.dismiss(toastId);
  },

  /**
   * Promise-based toast - perfect for async operations
   * Shows loading -> success/error based on promise resolution
   */
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: unknown) => string);
    },
    options?: ToastOptions
  ) => {
    return toast.promise(promise, messages, { ...defaultOptions, ...options });
  },

  /**
   * Show an info toast (custom styled)
   */
  info: (message: string, options?: ToastOptions) => {
    return toast(message, {
      ...defaultOptions,
      icon: 'ℹ️',
      style: {
        background: '#ffffff',
        color: '#1f2937',
        border: '1px solid rgba(99, 102, 241, 0.3)', // --accent-info
      },
      ...options,
    });
  },

  /**
   * Show a warning toast (custom styled)
   */
  warning: (message: string, options?: ToastOptions) => {
    return toast(message, {
      ...defaultOptions,
      icon: '⚠️',
      style: {
        background: '#ffffff',
        color: '#1f2937',
        border: '1px solid rgba(251, 191, 36, 0.3)', // --accent-warning
      },
      ...options,
    });
  },

  /**
   * Show a compact mini toast — small, subtle, short-lived.
   * Perfect for auto-save confirmations and non-critical feedback.
   */
  mini: (message: string, icon?: string, options?: ToastOptions) => {
    return toast(message, {
      ...defaultOptions,
      duration: 1500,
      icon: icon || '✓',
      style: {
        background: '#ffffff',
        color: '#6b7280',
        fontSize: '13px',
        padding: '8px 12px',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        minHeight: 'unset',
      },
      ...options,
    });
  },
};

export default toastService;
